/**
 * Shared vocabulary for the Customers list and the customer detail page.
 *
 * Both surfaces answer the same two questions — "is this customer healthy?"
 * and "where are they against their ceiling?" — and they must answer them
 * identically. A list that says a tenant is fine while its detail page says it
 * is blocked is worse than either screen alone.
 */
import { useTranslation } from '@papan/i18n'
import type { AdminTenantRow } from '../../client/budget'

/**
 * Spend against the ceiling, and the C4 blocked-vs-unlimited distinction.
 *
 * ── `null` and `0` are different states, and the UI must say so ─────────
 *
 * Throughout this system a cap of `null` means UNLIMITED and `0` means HARD
 * BLOCK. They are opposites, and both render as "no bar" if you only ask
 * whether a number is falsy — which is the bug this function exists to make
 * unwriteable at the call site.
 *
 * ── Why "AI blocked", never "suspended" (C4) ────────────────────────────
 *
 * There is no suspended state in this system — `tenants` has no status column.
 * A zero cap stops AI spend and nothing else: files, sharing and mini-apps all
 * keep working. Labelling it "suspended" would claim a capability the product
 * does not have, and would mislead the operator into thinking they had cut a
 * non-paying customer off when they had not. The honest label is the narrow
 * one. See docs/plans/2026-08-04_PLATFORM_ADMIN_ITEM_CATALOGUE.md §C4.
 */
export function capState(row: AdminTenantRow): {
  /** Combined ceiling in microdollars, or `null` when unlimited. */
  total: number | null
  /** Percentage of that ceiling consumed. 0 when there is no ceiling. */
  pct: number
  /** Every axis is a hard zero — AI spend is refused outright. */
  blocked: boolean
} {
  const free = row.freeCapBillingMicro
  const extra = row.extraCapBillingMicro
  // A hard block needs BOTH money axes at an explicit 0. One axis at 0 with the
  // other unlimited or positive still leaves headroom, so it is not a block.
  const blocked = free === 0 && extra === 0
  if (free == null || extra == null) {
    // Either axis unlimited ⇒ the pooled ceiling is unlimited. There is no
    // denominator, so there is no meaningful percentage.
    return { total: null, pct: 0, blocked: false }
  }
  const total = free + extra
  if (total === 0) return { total: null, pct: 0, blocked }
  return { total, pct: (row.periodCostBillingMicro / total) * 100, blocked }
}

/**
 * Subscription health, as one badge.
 *
 * The STATE is derived server-side (see `subscriptionView`) so its precedence —
 * disputed outranks past-due outranks canceling — lives in one place and
 * matches what `cost-check` actually enforces. This component only picks the
 * colour and the words.
 *
 * `none` renders nothing rather than a "Free" chip: the overwhelming majority
 * of tenants have no subscription, and a badge on every row would be noise that
 * buries the three that need attention. The plan name is already in the row.
 */
export function HealthBadge({ row }: { row: AdminTenantRow }) {
  const { t } = useTranslation('core-ui')
  if (row.health === 'none' || row.health === 'active') return null

  const style =
    row.health === 'disputed' || row.health === 'past_due'
      ? 'bg-red-500/20 text-red-300'
      : row.health === 'incomplete'
        ? 'bg-amber-500/20 text-amber-300'
        : 'themed-bg-tertiary themed-text-secondary'

  const label =
    row.health === 'disputed'
      ? t('settings.platform.health.disputed', { defaultValue: 'Payment disputed' })
      : row.health === 'past_due'
        ? t('settings.platform.health.pastDue', { defaultValue: 'Past due' })
        : row.health === 'incomplete'
          ? t('settings.platform.health.incomplete', { defaultValue: 'Awaiting payment' })
          : t('settings.platform.health.canceling', { defaultValue: 'Cancels at period end' })

  // Both red states PAUSE AI today. Saying so in the tooltip is the difference
  // between a badge that reports a billing fact and one that tells the operator
  // what their customer is currently experiencing.
  const hint =
    row.health === 'disputed' || row.health === 'past_due'
      ? t('settings.platform.health.paused', {
          defaultValue: 'AI is paused for this tenant until this is resolved.',
        })
      : undefined

  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${style}`}
      title={hint}
    >
      {label}
    </span>
  )
}

/**
 * "Uploads paused" — manual billing's write freeze (phase 3).
 *
 * ── Its own chip, NOT a `health` state ──────────────────────────────────
 *
 * `health` is one value with a precedence mirroring what `cost-check` enforces
 * about AI and payment. This restriction is orthogonal: a customer can be
 * AI-blocked, uploads-paused, both, or neither, and folding it into that ladder
 * would make whichever ranked lower invisible. Both chips appearing together is
 * the correct rendering of "both".
 *
 * Amber rather than red, and beside a red `past_due`: reads still work, edits
 * still work, and the customer has not lost access to anything they own. Calling
 * it a suspension in colour would be as wrong as calling it one in words.
 *
 * Renders nothing when the flag is absent — a pre-0170 API, and on the
 * overwhelming majority of deployments where no plan sets the feature at all.
 */
export function UploadsPausedChip({ row }: { row: AdminTenantRow }) {
  const { t } = useTranslation('core-ui')
  if (!row.uploadsPaused) return null
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap bg-amber-500/20 text-amber-300"
      title={t('settings.platform.health.pausedUploadsHint', {
        defaultValue:
          'Their plan blocks new files and folders. Reads and edits to existing files are unaffected.',
      })}
    >
      {t('settings.platform.health.pausedUploads', { defaultValue: 'Uploads paused' })}
    </span>
  )
}

/**
 * "Identity-only" — this personal tenant is not a workspace (0175, Phase D).
 *
 * ── Renders for the RESTRICTED state only ───────────────────────────────
 *
 * A "Full" chip on every row would be noise on every deployment that ships the
 * default, and noise is what makes the interesting chip easy to miss. The list
 * already names the tenant TYPE beneath, so a personal tenant with no chip is
 * unambiguously a full one.
 *
 * ── Its own chip, NOT a `health` state, and NOT the uploads-paused one ──
 *
 * Three orthogonal restrictions now: AI blocked (a zero cap), uploads paused
 * (manual billing's write freeze), and this. A tenant can be in any combination,
 * and folding any of them into `health`'s single-value ladder makes whichever
 * ranks lower invisible. All three appearing together is the correct rendering
 * of "all three".
 *
 * Distinct from "Uploads paused" in a way worth keeping straight: that one is
 * about NEW items in a drive the person still has, this one is about not having
 * the drive. Neither is a suspension — the account, the organizations and every
 * inbound share keep working — so neither is red. Slate rather than the freeze's
 * amber, because this is a configured shape rather than a payment condition an
 * operator is being asked to resolve.
 *
 * Reads `effectiveMode`, never `personalModeOverride`: the override is `null` on
 * every tenant that inherits an identity-only DEPLOYMENT default, so a chip
 * driven by the override would be absent on precisely the deployment where the
 * whole feature is switched on. Renders nothing when the field is absent (an API
 * instance that predates it, mid-rolling-restart).
 */
export function IdentityOnlyChip({ row }: { row: AdminTenantRow }) {
  const { t } = useTranslation('core-ui')
  if (row.effectiveMode !== 'identity-only') return null
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap bg-slate-500/25 themed-text-secondary"
      title={t('settings.platform.health.identityOnlyHint', {
        defaultValue:
          'No personal drive. They sign in, keep their account, work inside organizations, and can still be shared with.',
      })}
    >
      {t('settings.platform.health.identityOnly', { defaultValue: 'Identity-only' })}
    </span>
  )
}

/**
 * The enforcement-mode chip.
 *
 * `warn_only` softens ONLY the per-user axis, and only while the tenant still
 * has headroom — the tenant ceiling is never softened. So the honest warning is
 * not "nothing stops them" but "their members sail past their own caps and the
 * org hits the ceiling as one sudden stop".
 */
export function EnforcementChip({ mode }: { mode: 'enforce' | 'warn_only' }) {
  const { t } = useTranslation('core-ui')
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${
        mode === 'warn_only' ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
      }`}
      title={mode === 'warn_only'
        ? t('settings.budget.warnOnly.ceiling')
        : t('settings.budget.enforce.desc')}
    >
      {mode === 'warn_only' ? t('settings.budget.warnOnly.label') : t('settings.budget.enforce.label')}
    </span>
  )
}
